import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'ubiquity_portal_access_v1';

function readStoredAccess() {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const email = parsed?.email != null ? String(parsed.email).trim() : '';
    if (!email) return null;
    const scope = parsed?.scope != null ? String(parsed.scope).trim() : 'GLOBAL';
    return { email, scope };
  } catch (_) {
    return null;
  }
}

function writeStoredAccess(access) {
  try {
    if (access?.email) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ email: access.email, scope: access.scope || 'GLOBAL' }));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (_) {
    /* ignore quota / private mode */
  }
}

const PortalAccessContext = createContext(null);

export function PortalAccessProvider({ children }) {
  const [access, setAccessState] = useState(() => readStoredAccess());

  const setPortalAccess = useCallback((next) => {
    if (!next?.email) {
      setAccessState(null);
      writeStoredAccess(null);
      return;
    }
    const normalized = {
      email: String(next.email).trim(),
      scope: String(next.scope || 'GLOBAL').trim() || 'GLOBAL',
    };
    setAccessState(normalized);
    writeStoredAccess(normalized);
  }, []);

  const clearPortalAccess = useCallback(() => {
    setAccessState(null);
    writeStoredAccess(null);
  }, []);

  const value = useMemo(
    () => ({
      access,
      setPortalAccess,
      clearPortalAccess,
    }),
    [access, setPortalAccess, clearPortalAccess]
  );

  return <PortalAccessContext.Provider value={value}>{children}</PortalAccessContext.Provider>;
}

export function usePortalAccess() {
  const ctx = useContext(PortalAccessContext);
  if (!ctx) {
    throw new Error('usePortalAccess must be used within PortalAccessProvider');
  }
  return ctx;
}
